import { NewStatusPageLoadingPanel } from '../pages/NewStatusPage/NewStatusPageLoadingPanel';
import { getPageUrlDic, Page } from './routes';

// Loading-shell kind only. Paths come from Page + getPageUrlDic, never from these values.
export enum StatusLoadingRoute {
  AllFields = NewStatusPageLoadingPanel.AllFields,
  FieldDetails = NewStatusPageLoadingPanel.FieldDetails,
  Nutrition = 'Nutrition',
  TableGeneral = 'TableGeneral',
}

const PLACEHOLDER_FARM_UUID = '__status-loading-farm__';

// Which Page shows which loading shell. Rename a route URL in routes.ts only.
const STATUS_LOADING_ROUTE_BY_PAGE = {
  [Page.STATUS_NEW_ALL_FIELDS_NUTRITION]: StatusLoadingRoute.Nutrition,
  [Page.STATUS_TABLE_ALL_FIELDS_GENERAL]: StatusLoadingRoute.TableGeneral,
  [Page.STATUS_NEW_ALL_FIELDS]: StatusLoadingRoute.AllFields,
  [Page.STATUS_NEW_FIELD_DETAILS]: StatusLoadingRoute.FieldDetails,
} as const satisfies Partial<Record<Page, StatusLoadingRoute>>;

const TABLE_STATUS_LOADING_ROUTES = new Set<StatusLoadingRoute>([
  StatusLoadingRoute.TableGeneral,
  StatusLoadingRoute.Nutrition,
]);

const pageUrlDic = getPageUrlDic({
  prefix: '',
  farmUuid: PLACEHOLDER_FARM_UUID,
});

const getPagePathSuffix = (page: Page): string => {
  const pageUrl = pageUrlDic[page];
  const farmScopedPrefix = `/farm/${PLACEHOLDER_FARM_UUID}`;

  return pageUrl.startsWith(farmScopedPrefix)
    ? pageUrl.slice(farmScopedPrefix.length)
    : pageUrl;
};

const STATUS_LOADING_ROUTE_SUFFIXES = (
  Object.entries(STATUS_LOADING_ROUTE_BY_PAGE) as Array<
    [Page, StatusLoadingRoute]
  >
)
  .map(([page, loadingRoute]) => ({
    suffix: getPagePathSuffix(page),
    loadingRoute,
  }))
  .sort((left, right) => right.suffix.length - left.suffix.length);

export const getStatusLoadingRoute = (
  pathname: string,
): StatusLoadingRoute | undefined =>
  STATUS_LOADING_ROUTE_SUFFIXES.find(({ suffix }) => pathname.endsWith(suffix))
    ?.loadingRoute;

export const isNewStatusLoadingRoute = (
  loadingRoute: StatusLoadingRoute | NewStatusPageLoadingPanel | undefined,
): loadingRoute is NewStatusPageLoadingPanel =>
  loadingRoute === NewStatusPageLoadingPanel.AllFields ||
  loadingRoute === NewStatusPageLoadingPanel.FieldDetails;

export const isTableStatusLoadingRoute = (
  loadingRoute: StatusLoadingRoute | undefined,
) =>
  loadingRoute !== undefined && TABLE_STATUS_LOADING_ROUTES.has(loadingRoute);

export const pathnameMatchesPage = (pathname: string, page: Page): boolean => {
  const suffix = getPagePathSuffix(page);

  return suffix.length > 0 && pathname.endsWith(suffix);
};

export const isStatusPathname = (pathname: string) => {
  const statusRootSuffix = getPagePathSuffix(Page.STATUS);

  return statusRootSuffix.length > 0 && pathname.includes(statusRootSuffix);
};

export const isStatusFieldDetailsPathname = (pathname: string) =>
  pathnameMatchesPage(pathname, Page.STATUS_NEW_FIELD_DETAILS);
